import { withZod } from '@remix-validated-form/with-zod'
import { WarningHexagon } from 'iconoir-react'
import { isEmpty } from 'lodash'
import { ReactElement, useEffect, useState } from 'react'
import { z } from 'zod'
import { Button } from 'app/components/ui/button'
import { DialogActionBar } from 'app/components/ui/dialog'
import FormArrayInput from '../form/FormArrayInput'
import FormComboAsyncV2 from '../form/FormComboAsyncV2'
import FormInput from '../form/FormInput'
import { SmartForm, SubAction } from '../form/SmartForm'
import type { StaffJson, StaffType } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import { logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'
import { gqlFetchOne, gqlFetchList, gqlMutation } from 'app/lib/graphqlClient'
import { graphql } from 'app/generated/gql'

const CREATE_STAFF_MUTATION = graphql(`
  mutation CreateStaff($input: CreateStaffInput!) {
    createStaff(input: $input) {
      id
      name
      aliases
      userId
      error
      isDuplicated
    }
  }
`)

const UPDATE_STAFF_MUTATION = graphql(`
  mutation StaffFormUpdateStaff($id: ID!, $input: UpdateStaffInput!) {
    updateStaff(id: $id, input: $input) {
      id
      name
      aliases
      userId
      isDuplicated
    }
  }
`)

const DELETE_STAFF_MUTATION = graphql(`
  mutation StaffFormDeleteStaff($id: ID!) {
    deleteStaff(id: $id)
  }
`)

// Extend StaffType to include associatedUser for form purposes
type StaffFormType = StaffType & { associatedUser?: { id: string; name: string; value: string } }

/** Fetch a user by ID with minimal fields — avoids the full UserStore query which includes throwing fields like role */
export async function fetchStaffUser(userId: string): Promise<{ id: string; name: string } | undefined> {
  return gqlFetchOne<{ id: string; name: string }>(`user`, userId, `id name`)
}

const EMPTY_STRING_ARRAY: string[] = []

const staffValidator = withZod(
  z.object({
    name: z.string().min(1, { message: `Name is required` }),
    associatedUser: z.object({ id: z.string(), name: z.string(), value: z.string() }).optional(),
    aliases: z
      .union([z.string(), z.array(z.string())])
      .optional()
      .transform((val) => {
        if (!val) return undefined
        if (typeof val === `string`) return val ? [val] : undefined
        return val.filter(Boolean)
      }),
  }),
)

export function StaffForm({
  staff,
  loading = false,
  onSubmit,
  onDelete,
  excludeUserIds = EMPTY_STRING_ARRAY,
  hideAssociatedUser = false,
}: {
  staff?: StaffJson
  loading?: boolean
  onSubmit?: (data: { staff: Partial<StaffJson> }) => void
  onDelete?: () => void
  excludeUserIds?: string[]
  hideAssociatedUser?: boolean
}): ReactElement {
  const [isLoading, setIsLoading] = useState<boolean>(loading)
  const [isDeleting, setIsDeleting] = useState(false)
  const me = useUser()
  if (!me) return <></>
  const [defaultUser, setDefaultUser] = useState<OptionType | undefined>()
  const [subActionState, setSubActionState] = useState<SubAction | undefined>()
  const [initialUserLoaded, setInitialUserLoaded] = useState<boolean>(false)
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false)

  const handleSubmit = async (data: StaffJson): Promise<void> => {
    const subAction = isEmpty(staff) || staff?.id == null ? SubAction.CREATE : SubAction.EDIT
    setIsLoading(true)
    setIsDuplicate(false)
    const result = await staffValidator.validate(data)

    if (result.error) {
      logError(`error saving staff`, result.error)
      notifyError(`Error Saving`)
      setIsLoading(false)
      return
    }

    // Process aliases - validator already transforms to array
    // Remove associatedUser and add userId instead
    const { associatedUser, ...dataWithoutAssociatedUser } = result.data
    const processedData = {
      ...dataWithoutAssociatedUser,
      userId: associatedUser && associatedUser.id !== `-1` ? associatedUser.id : null,
      // Send empty array when aliases are cleared, undefined when not provided
      aliases: result.data.aliases?.map((a: string) => a.trim()).filter(Boolean) ?? [],
    }

    if (subAction === SubAction.CREATE) {
      const result = await gqlMutation(CREATE_STAFF_MUTATION, { input: { ...processedData, active: true } })

      if (result.error) {
        logError(`Error creating staff`, result.error)
        notifyError(`Error creating staff`)
        setIsLoading(false)
        return
      }

      const response = result.data?.createStaff

      if (response?.error || response?.isDuplicated) {
        setIsLoading(false)
        setIsDuplicate(!!response?.isDuplicated)
        return
      }

      notifySuccess(`Staff Member Created`)
      onSubmit?.({ staff: response } as any)
      closeModal()
    } else if (subAction === SubAction.EDIT && staff?.id) {
      const result = await gqlMutation(UPDATE_STAFF_MUTATION, {
        id: staff.id,
        input: processedData,
      })

      if (result.error) {
        logError(`Error updating staff`, result.error)
        notifyError(`Error updating staff`)
        setIsLoading(false)
        return
      }

      const response = result.data?.updateStaff

      if (response?.isDuplicated) {
        setIsLoading(false)
        setIsDuplicate(true)
        return
      }

      notifySuccess(`Staff Member Updated`)
      onSubmit?.({ staff: response } as any)
      closeModal()
    }
  }

  const handleDelete = async () => {
    if (!staff?.id) return

    setIsDeleting(true)
    const result = await gqlMutation(DELETE_STAFF_MUTATION, {
      id: staff.id,
    })
    setIsDeleting(false)

    if (result.error) {
      notifyError(result.error?.message ?? `Error deleting staff`)
    } else {
      notifySuccess(`Staff Member Deleted`)
      onDelete?.()
      closeModal()
    }
  }

  useEffect(() => {
    if (isEmpty(staff) || staff?.id == null) {
      setSubActionState(SubAction.CREATE)
    } else {
      setSubActionState(SubAction.EDIT)
      // Fetch the associated user data if staff has a userId
      if (staff.userId && !initialUserLoaded) {
        setInitialUserLoaded(true)
        fetchStaffUser(staff.userId)
          .then((user) => {
            if (user) {
              setDefaultUser({ id: user.id, name: user.name ?? ``, value: user.id })
            }
          })
          .catch(() => {
            // User might not exist anymore, that's ok
          })
      } else if (!staff.userId && !initialUserLoaded) {
        // If there's no userId, set to None option
        setInitialUserLoaded(true)
        setDefaultUser({ id: `-1`, name: `None`, value: `-1` })
      }
    }
  }, [staff, initialUserLoaded])

  return (
    <SmartForm<StaffFormType>
      key={`staff-form-${staff?.id || `new`}-${initialUserLoaded}`}
      id="staff-form"
      validator={staffValidator}
      className="mb-0 mt-0 flex w-screen flex-col gap-y-4 pt-4 md:w-157"
      defaultValues={
        {
          name: staff?.name || ``,
          aliases: staff?.aliases?.length ? staff.aliases : undefined,
          associatedUser:
            defaultUser || (!staff?.userId && staff?.id ? { id: `-1`, name: `None`, value: `-1` } : undefined),
        } as Partial<StaffFormType>
      }
      onSubmit={(data) => handleSubmit(data as StaffJson)}
      hiddenInputs={[{ name: `id`, value: staff?.id }]}
      hideButtons
      preventSubmit
    >
      {/* col-span-6: SmartForm's container is a 6-column grid at >=sm (its own
          children carry col-span-6 too). Without it these blocks land in a single
          ~87px track, which crushed the fields and footer on desktop. */}
      <div className="col-span-6 flex flex-col gap-y-4 px-6">
        <FormInput cols={6} name="name" label="Staff Name" labelClassName="text-ui-sm text-default required" required />
        {!hideAssociatedUser && (
          <FormComboAsyncV2
            cols={6}
            name="associatedUser"
            label="Associated User"
            labelClassName="text-ui-sm"
            optionsMapper={(res) => {
              // Filter out users already assigned to other staff (excluding current staff being edited)
              let options = res.hits
                .filter((hit) => !excludeUserIds.includes(hit.id!) || hit.id === staff?.userId)
                .map((hit) => {
                  return { id: hit.id!, name: hit.name ?? ``, value: hit.id! }
                })
              return [{ id: `-1`, name: `None`, value: `-1` }, ...options]
            }}
            fetcher={(params) =>
              gqlFetchList(
                'users',
                {
                  search: [params.query || undefined, `workspaceId:${me.workspaceId}`, `!type:super`]
                    .filter(Boolean)
                    .join(' AND '),
                  pagination: { limit: 100 },
                },
                'id name',
              )
            }
            value={defaultUser}
            onChange={(option) => {
              setDefaultUser(option)
            }}
            placeholder="Select a user"
          />
        )}
        <FormArrayInput
          name="aliases"
          label="Aliases"
          placeholder="Enter alias"
          labelClassName="text-ui-sm text-default"
          addButtonLabel="Add New Alias"
          defaultValue={staff?.aliases}
        />
      </div>
      <DialogActionBar
        className="col-span-6"
        start={
          <>
            {subActionState === SubAction.EDIT && (
              <Button
                type="button"
                variant="danger"
                size="md"
                onClick={handleDelete}
                disabled={isDeleting || isLoading}
                loading={isDeleting}
              >
                Delete Staff
              </Button>
            )}
            {isDuplicate && (
              <div className="inline-flex items-center gap-x-2 text-red-700">
                <WarningHexagon className="size-4" />
                <span className="text-body-xs">Staff name or alias already exists</span>
              </div>
            )}
          </>
        }
      >
        <Button type="button" variant="secondary" size="md" onClick={() => closeModal()} disabled={isLoading}>
          Cancel
        </Button>
        {subActionState && (
          <Button type="submit" variant="primary" size="md" loading={isLoading || isDeleting}>
            {subActionState === SubAction.CREATE ? `Create` : `Update`}
          </Button>
        )}
      </DialogActionBar>
    </SmartForm>
  )
}
